import { Avatar, Badge, Box, ListItem, ListItemAvatar, ListItemText, Typography } from '@mui/material';
import React, { useState } from 'react';
import { chatPhotoUrl, uploadedFileUrl } from '../../utils/constants/links';

const ChatListItem = ({ chat, onClick }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const isMe = chat.participants?.[0]?.name !== chat.last_message_sender;
  const secondaryText =
    chat.type === 'GROUP'
      ? chat.last_message_sender && chat.last_message
        ? `${chat.last_message_sender} : ${chat.last_message}`
        : 'You was added'
      : chat.last_message
        ? chat.last_message
        : 'Start a new conversation';

  const avatarImage =
    chat.type === 'GROUP'
      ? chat?.group_photo
        ? `${uploadedFileUrl}/${chat?.group_photo}`
        : null
      : chat.participants[0]?.photo
        ? `${chatPhotoUrl}/${chat.participants[0]?.photo}.jpg`
        : null;

  const formatChatTime = (dateStr) => {
    if (!dateStr) return '';

    const date = new Date(dateStr.replace(' ', 'T'));
    const now = new Date();

    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    const yesterday = new Date();
    yesterday.setDate(now.getDate() - 1);

    const isYesterday =
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();

    if (isToday) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }

    if (isYesterday) {
      return 'Yesterday';
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const name = chat.type === 'GROUP' ? chat.group_name : chat.participants?.[0]?.name;
  const isOnline = chat.type === 'GROUP' ? chat.status : chat.participants?.[0]?.is_online;
  const hasUnread = chat.unread_count > 0;

  React.useEffect(() => {
    if (!avatarImage) {
      setImageLoaded(false);
      return;
    }

    const img = new Image();
    img.src = avatarImage;

    img.onload = () => {
      setImageLoaded(true);
    };

    img.onerror = () => {
      setImageLoaded(false);
    };

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [avatarImage]);

  return (
    <ListItem
      onClick={() => onClick(chat?.chat_room_id)}
      sx={{
        mb: 0.8,
        px: 1.5,
        py: 1.1,
        borderRadius: 2,
        minHeight: 58,
        bgcolor: '#ffffff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        cursor: 'pointer',
        transition: 'all 0.18s ease',
        border: '1px solid transparent',
        // borderLeft:"3px solid transparent",

        '&:hover': {
          border: '1px solid #561f6e',
          // borderLeft:"3px solid #561f6e",
          // bgcolor: '#ffffff3b',
          boxShadow: '2px 4px 12px rgba(86,31,110,0.12)',
        },
      }}
    >
      <ListItemAvatar>
        <Badge
          variant="dot"
          color={isOnline ? 'success' : 'default'}
          overlap="circular"
          invisible={!isOnline}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          {/* <Avatar sx={{ width: 50, height: 50 }} src={avatarImage}>
            {name[0]?.toUpperCase()}
          </Avatar> */}
          <Box
            sx={{
              p: '0.5px',
              borderRadius: '50%',
              background: '#f0eff0',
            }}
          >
            <Avatar
              sx={{
                width: 50,
                height: 50,
                bgcolor: '#561f6e',
                fontWeight: 600,
                '& img': {
                  animation: imageLoaded ? 'fadeIn 0.25s ease' : 'none',
                },
              }}
              src={imageLoaded ? avatarImage : undefined}
            >
              {name?.[0]?.toUpperCase()}
            </Avatar>
          </Box>
        </Badge>
      </ListItemAvatar>

      <ListItemText
        sx={{ ml: 1 }}
        primaryTypographyProps={{ component: 'div' }}
        secondaryTypographyProps={{ component: 'div' }}
        primary={
          <Box display="flex" justifyContent="space-between">
            <Typography fontWeight={500} noWrap>
              {name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                ml: 1,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {formatChatTime(chat.last_message_time)}
            </Typography>
          </Box>
        }
        secondary={
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 190, ml: 0.2 }}>
              {secondaryText ?? 'Start exploring conversations'}
            </Typography>

            {hasUnread && (
              <Box
                sx={{
                  ml: 1,
                  minWidth: 18,
                  height: 18,
                  p: 0.7,
                  borderRadius: '999px',
                  bgcolor: '#0089d0',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {chat.unread_count}
              </Box>
            )}
          </Box>
        }
      />
    </ListItem>
  );
};

export default ChatListItem;
